import { joinClassNames } from '@vertex-protocol/web-common';
import { Spinner } from '@vertex-protocol/web-ui';
import { BridgeMode } from '../types';

interface Props {
  selectedMode: BridgeMode;
  onModeChange: (mode: BridgeMode) => void;
  /** Formatted Across relay fee (e.g. "~$0.001 USDC") or undefined while loading */
  acrossFee: string | undefined;
  isLoading?: boolean;
  /** When true, selector is visible but not interactive (flow already started) */
  disabled?: boolean;
}

/**
 * Bridge mode selector for USDC cross-chain transfers.
 * Two side-by-side cards: Fast (Across, ~2s) vs Free (CCTP, ~20-60 min).
 */
export function BridgeModeSelector({
  selectedMode,
  onModeChange,
  acrossFee,
  isLoading,
  disabled,
}: Props) {
  return (
    <div className={joinClassNames('flex gap-x-2', disabled && 'opacity-60 pointer-events-none')}>
      <ModeCard
        selected={selectedMode === 'fast'}
        onClick={() => onModeChange('fast')}
        label="Fast"
        icon="⚡"
        badge="~2 sec"
        badgeVariant="fast"
        fee={isLoading ? 'Loading...' : (acrossFee ?? '~$0')}
        provider="via Across Protocol"
      />
      <ModeCard
        selected={selectedMode === 'free'}
        onClick={() => onModeChange('free')}
        label="Free"
        badge="20–60 min"
        badgeVariant="free"
        fee="$0.00"
        provider="via Circle CCTP"
      />
    </div>
  );
}

function ModeCard({
  selected,
  onClick,
  label,
  icon,
  badge,
  badgeVariant,
  fee,
  provider,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
  icon?: string;
  badge: string;
  badgeVariant: 'fast' | 'free';
  fee: string;
  provider: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={joinClassNames(
        'flex flex-1 flex-col gap-y-1 rounded-sm border p-2.5 transition-colors text-left',
        selected
          ? 'border-accent bg-surface-2'
          : 'border-stroke bg-surface-1 hover:border-disabled',
      )}
    >
      {/* Header: radio + label + badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1.5">
          <RadioDot selected={selected} />
          <span className="text-text-primary text-xs font-semibold">
            {icon && <span className="mr-0.5">{icon}</span>}
            {label}
          </span>
        </div>
        <span
          className={joinClassNames(
            'rounded px-1.5 py-0.5 text-3xs font-bold uppercase tracking-wide',
            badgeVariant === 'fast'
              ? 'bg-accent/15 text-accent'
              : 'bg-positive/15 text-positive',
          )}
        >
          {badge}
        </span>
      </div>

      {/* Details */}
      <div className="flex flex-col gap-y-0.5 pl-5">
        <span
          className={joinClassNames(
            'text-2xs',
            selected ? 'text-text-secondary' : 'text-text-tertiary',
          )}
        >
          Fee: {fee}
        </span>
        <span
          className={joinClassNames(
            'text-2xs',
            selected ? 'text-text-secondary' : 'text-text-tertiary',
          )}
        >
          {provider}
        </span>
      </div>
    </button>
  );
}

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <div
      className={joinClassNames(
        'flex size-3.5 items-center justify-center rounded-full border-[1.5px] flex-shrink-0',
        selected ? 'border-accent' : 'border-disabled',
      )}
    >
      {selected && <div className="bg-accent size-2 rounded-full" />}
    </div>
  );
}
